/**
 * Customer-portal locale bundles.
 *
 * Keep keys flat (`'nav.dashboard'`) so the lookup is trivial and the
 * bundle is grep-friendly. Translations are stored inline here so the
 * code-splitter ships them alongside the app — no async fetch on
 * first render. `{name}` placeholders are filled by `t(key, params)`.
 */

export const SUPPORTED_LOCALES = ['en', 'es'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

const en = {
  // Navigation
  'nav.dashboard': 'Dashboard',
  'nav.locations': 'Locations',
  'nav.trends': 'Trends',
  'nav.tickets': 'Tickets',
  'nav.documents': 'Documents',
  'nav.billing': 'Billing',
  'nav.network': 'Network',
  'nav.services': 'Services',
  'nav.systems': 'Systems',
  'nav.reports': 'Reports',
  'nav.activity': 'Activity',
  'nav.all_locations': 'All locations',
  'nav.sign_out': 'Sign out',
  'nav.section.overview': 'Overview',
  'nav.section.site': 'Site',
  'nav.section.support': 'Support',
  'nav.section.account': 'Account',
  'nav.badge_open': 'open',

  // Notification centre
  'notifications.title': 'Notifications',
  'notifications.empty': 'Nothing new. Updates on your tickets, visits and documents appear here.',
  'notifications.mark_all_read': 'Mark all read',
  'notifications.unread': 'Unread',
  'notifications.aria_none': 'Notifications',
  'notifications.aria_unread': 'Notifications, {n} unread',

  // Calendar chrome
  // Site manuals (Zoho Learn)
  'location.manuals.title': 'Site documentation',
  'location.manuals.subtitle': 'The manuals PazNetworks maintains for this property in PazNet Learn.',
  'location.manuals.property': 'Property manual',
  'location.manuals.property_hint': 'Network layout, systems, standing procedures and site contacts.',
  'location.manuals.engineering': 'Engineering and facilities manual',
  'location.manuals.engineering_hint': 'Plant, riser and facilities detail for this property.',
  'location.manuals.opens_in_learn': 'Opens in PazNet Learn in a new tab',

  // Command palette
  'palette.title': 'Search and commands',
  'palette.placeholder': 'Search sites, tickets and pages…',
  'palette.results': 'Results',
  'palette.empty': 'Nothing matches that.',
  'palette.footer': 'Arrow keys to move · Enter to open · Esc to close',
  'palette.kind_page': 'Page',
  'palette.kind_site': 'Site',
  'palette.kind_ticket': 'Ticket',
  'palette.kind_action': 'Action',
  'palette.action_new_ticket': 'Open a support ticket',
  'palette.action_trends': 'View portfolio trends',

  'documents.filter_label': 'Filter documents by category',
  'documents.filter_all_sites': 'All sites',

  // Dashboard layout
  'dashboard.customise': 'Customise',
  'dashboard.customise_title': 'Dashboard sections',
  'dashboard.customise_hidden': '{n} hidden',
  'dashboard.customise_reset': 'Reset',
  'dashboard.customise_hint': 'Saved to your account, on every device you sign in from.',
  'dashboard.customise_move_up': 'Move {name} up',
  'dashboard.customise_move_down': 'Move {name} down',
  'dashboard.widget.metrics': 'Summary tiles',
  'dashboard.widget.sla': 'SLA compliance',
  'dashboard.widget.trips': 'Visit calendar',

  // Property mission control
  'location.availability_trend': 'Availability, last 30 days',
  'location.availability_trend_empty': 'Not enough samples yet for a trend.',
  'location.availability_trend_point_dip': '{date} · dipped to {worst}%',
  'location.last_visit_on': 'Last visit {date}',
  'location.timeline.title': 'Site history',
  'location.timeline.subtitle': 'What we delivered here, and what is scheduled next.',
  'location.timeline.delivered': 'Delivered',
  'location.timeline.scheduled': 'Scheduled',
  'location.timeline.on_site': 'On site now',
  'location.timeline.empty': 'No visits recorded for this site yet.',

  // Portfolio command view
  'dashboard.sites_title': 'Portfolio status',
  'dashboard.sites_subtitle': 'WAN uptime for each network. Select a site to open it.',
  'dashboard.sites_devices': '{online}/{total} online',
  'dashboard.sites_live': 'Live',
  'dashboard.sites_down': 'Down',
  'dashboard.sites_uptime': '{pct} uptime',
  'dashboard.sites_no_telemetry': 'Not monitored',
  'dashboard.sites_telemetry_lost': 'Monitoring offline · last seen {age}',
  'dashboard.sites_legend': 'Site state:',
  'dashboard.sites_state_good': 'Live',
  'dashboard.sites_state_warning': 'Needs attention',
  'dashboard.sites_state_serious': 'Degraded',
  'dashboard.sites_state_critical': 'Down',
  'dashboard.sites_state_unknown': 'Not monitored',
  'dashboard.exceptions_title': 'Networks down',
  'dashboard.exceptions_none': 'Every monitored network is live.',
  'dashboard.exceptions_tickets': '{n} open',
  'dashboard.systems_state_operational': 'Operational',
  'dashboard.systems_state_degraded': 'Degraded',
  'dashboard.systems_state_offline': 'Offline',
  'calendar.previous': 'Previous period',
  'calendar.next': 'Next period',
  'calendar.remove': 'Remove',
  'calendar.visits_in_window_one': '1 visit in this window',
  'calendar.visits_in_window': '{n} visits in this window',
  'property.trips.select_property': 'Select property…',
  'property.trips.title_placeholder': 'e.g. ACC Tip-Off coverage',
  'property.trips.notes_placeholder': 'Coverage notes for PazNetworks',

  // Shell
  'shell.all_accounts': 'All accounts',
  'shell.locations_count': '{n} locations',
  'shell.location_count_one': '1 location',
  'shell.loading': 'Loading…',

  // Language
  'lang.label': 'Language',
  'lang.en': 'English',
  'lang.es': 'Español',

  // Account switcher
  'account.label': 'Account',
  'account.all': 'All accounts',
  'account.all_count': 'All accounts ({n})',
  'account.portfolio_hint': 'Showing every linked account',
  'account.none': 'No customer account assigned',
  'access.invitations': 'Customer account invitations',
  'access.invitation_hint': 'Accept an invitation to access that customer account.',
  'access.accept': 'Accept invitation',
  'access.accept_for': 'Accept invitation for {name}',
  'access.accepting': 'Accepting…',
  'access.accept_failed': 'This invitation could not be accepted. It may have changed or been revoked. Refresh and try again.',
  'access.unavailable': 'We could not verify your current account access. Refresh to try again.',
  'access.refresh': 'Refresh access',
  'access.loading': 'Checking account access…',
  'account.none_hint': 'Ask a PazNetworks administrator to link this login to a customer account.',
  'account.error': 'Could not load your accounts.',

  // Staff view-as
  'viewas.viewing_as': 'Viewing as',
  'viewas.accounts': '{n} accounts',
  'viewas.account_one': '1 account',
  'viewas.exit': 'Exit view',
  'viewas.loading': 'Loading portal user…',
  'viewas.audit_failed': 'Audit entry could not be recorded',
  'viewas.read_only': 'Read-only: actions are disabled while viewing the portal as a customer.',
  'viewas.region': 'Staff view-as banner',

  // Staff picker
  'staff.picker.title': 'Choose a portal user',
  'staff.picker.subtitle':
    'Open the portal exactly as one of your customers sees it. Every page is scoped to the accounts linked to the user you choose, and the session is recorded in the audit log.',
  'staff.picker.search': 'Search by name, email, or account',
  'staff.picker.no_results': 'No portal users match your search.',
  'staff.picker.empty': 'No portal users have account links yet.',
  'staff.picker.error': 'Could not load portal users.',
  'staff.picker.last_login': 'Last sign-in',
  'staff.picker.never': 'Never',
  'staff.picker.view': 'View portal',
  'staff.picker.back_admin': 'Back to admin',
  'staff.picker.not_found': 'That portal user could not be found. Choose another.',
  'staff.picker.more': '+{n} more',
  'staff.picker.count': '{n} portal users',
  'staff.picker.count_one': '1 portal user',

  // Dashboard
  'dashboard.locations': 'Locations',
  'dashboard.locations_mapped': '{n} with network telemetry',
  'dashboard.network': 'Network',
  'dashboard.network_devices_online': '{online} of {total} devices online',
  'dashboard.network_sites_live': '{live} of {total} live',
  'dashboard.network_sites_down': '{n} down',
  'dashboard.network_all_live': 'Every monitored network is live',
  'dashboard.network_offline': '{n} offline',
  'dashboard.network_clients': '{n} connected clients',
  'dashboard.network_ssids': '{n} SSIDs',
  'dashboard.network_sites': '{n} UniFi sites',
  'dashboard.network_none': 'No network telemetry yet',
  'dashboard.network_none_hint': 'UniFi sites are not mapped to these locations yet.',
  'dashboard.network_source': 'UniFi',
  'dashboard.open_tickets': 'Open tickets',
  'dashboard.open_tickets_hint': 'Across selected accounts',
  'dashboard.renewal': 'Contract renewal',
  'dashboard.renewal_days': '{n} days',
  'dashboard.renewal_none': 'No renewal date on file',
  'dashboard.renewal_past': 'Renewal date has passed',
  'dashboard.alerts': 'Network alerts',
  'dashboard.alerts_not_connected': 'Auvik not connected',
  'dashboard.alerts_none': 'No open alerts',
  'dashboard.alerts_open': 'Open alerts',
  'dashboard.operational_health': 'Operational health',
  'dashboard.operational_health_subtitle':
    'Scored from tickets, SLA deadlines and network availability.',
  'dashboard.health_not_enough': 'Not enough data',
  'dashboard.health_not_enough_hint':
    'A score appears once tickets and network telemetry have been collected for this account.',
  'dashboard.health_of_100': 'of 100',
  'dashboard.health_computed': 'Computed {ago}',
  'dashboard.uptime': 'Network availability',
  'dashboard.tickets': 'Tickets',
  'dashboard.sla': 'SLA',
  'dashboard.sla_compliance': 'SLA compliance',
  'dashboard.sla_met': '{met} of {total} deadlines met',
  'dashboard.sla_none': 'No tickets with an SLA deadline yet',
  'dashboard.sla_tier': 'SLA tier',
  'dashboard.installed_platforms': 'Installed systems',
  'dashboard.active_field_project': 'Active field project',
  'dashboard.recent_activity': 'Recent activity',
  'dashboard.activity_empty': 'No recent activity',
  'dashboard.activity_empty_hint':
    'Ticket updates, field visits and shared documents will appear here.',
  'dashboard.activity_error': 'Could not load recent activity.',
  'dashboard.error': 'Could not load your dashboard.',
  'dashboard.offline_since': 'offline since',
  'dashboard.offline': 'offline',
  'dashboard.dismiss': 'Dismiss',
  'dashboard.actor.system': 'System',
  'dashboard.actor.paznetworks': 'PazNetworks',
  'dashboard.actor.customer': 'Your team',

  // Locations
  'locations.title': 'Locations',
  'locations.subtitle_count': '{n} sites',
  'locations.subtitle_count_one': '1 site',
  'locations.for_account': 'for {name}',
  'locations.map_title': 'Property map',
  'locations.map_subtitle': 'Select a site marker to open its resources.',
  'locations.map_choose': 'Choose a mapped site',
  'locations.map_unavailable': 'Interactive map unavailable',
  'locations.map_unavailable_hint':
    'This browser cannot render WebGL. Sites with map coordinates are listed instead.',
  'locations.list_title': 'Sites',
  'locations.list_subtitle': 'Tickets, network telemetry and documents for each site.',
  'locations.search': 'Search sites',
  'locations.no_match': 'No sites match “{q}”.',
  'locations.error_title': 'Unable to load locations',
  'locations.error_subtitle': 'Refresh the page or contact PazNetworks support.',
  'locations.empty.title': 'No locations configured yet',
  'locations.empty.subtitle':
    'Your PazNetworks account manager will add your sites during onboarding.',
  'locations.card.type': 'Type',
  'locations.card.network': 'Network',
  'locations.card.open_tickets': 'Open tickets',
  'locations.card.timezone': 'Time zone',
  'locations.card.no_telemetry': 'Not monitored',
  'locations.card.live': 'Live',
  'locations.card.down': 'Down',
  'locations.card.devices': '{online}/{total} online',
  'locations.address_missing': 'Address not provided',
  'locations.open_site': 'Open site page',

  // Property resources (map detail panel)
  'property.resources.site': 'Site resources',
  'property.resources.close': 'Close site details',
  'property.resources.tickets': 'Support tickets',
  'property.resources.new_ticket': 'New ticket',
  'property.resources.no_open_tickets': 'No open tickets for this site.',
  'property.resources.view_all_tickets': 'View all tickets',
  'property.resources.network_title': 'UniFi network',
  'property.resources.network_none': 'No UniFi telemetry for this site yet.',
  'property.resources.devices_online': 'devices online',
  'property.resources.clients': 'clients',
  'property.resources.ssids': 'SSIDs',
  'property.resources.operations': 'Network & operations',
  'property.resources.mission_control': 'View this site’s network status in Portal',
  'property.resources.auvik': 'Open this site in Auvik (network monitoring)',
  'property.resources.unifi': 'Open this site’s UniFi console',
  'property.resources.vendor_website': 'Communications vendor · public website',
  'property.resources.additional_networks': 'Additional networks',
  'property.resources.guest_experience': 'Guest experience',
  'property.resources.rankings_title': 'Rankings',
  'property.resources.rankings': 'Hotel rankings, trends, and history',
  'property.resources.ordering': 'Guest ordering menu',
  'property.resources.knowledge': 'Knowledge & site records',
  'property.resources.external_learn': 'Shared site manual · password may be required',
  'property.resources.engineering': 'Engineering & facilities',
  'property.resources.engineering_description': 'Site engineering manual',
  'property.resources.missing_links': 'Links not configured',
  'property.resources.not_linked': 'No link is on file for this site yet.',
  'property.resources.no_directory': 'This site is not linked to the property directory yet.',
  'property.resources.new_tab': '(opens in a new tab)',
  'property.resources.sign_in':
    'Links open in a new tab. Some services require sign-in or a sharing password.',

  // Location detail
  'location.back': 'Back to locations',
  'location.not_found': 'Location not found',
  'location.not_found_hint': 'This location may have been removed or belongs to another account.',
  'location.tab.overview': 'Overview',
  'location.tab.systems': 'Systems',
  'location.tab.network': 'Network',
  'location.tab.services': 'Services',
  'location.services.title': 'Circuits and services',
  'location.services.hint': 'Reviewed connectivity and provider information for this site.',
  'location.services.empty': 'No service details have been published for this site yet.',
  'location.services.error': 'Service details could not be loaded.',
  'location.services.service': 'Service',
  'location.services.circuit': 'Circuit ID',
  'location.services.bandwidth': 'Bandwidth',
  'location.services.transport': 'Transport / handoff',
  'location.services.sla': 'Service commitment',
  'location.services.restoration': 'Restoration target: {n} hours',
  'location.visits.title': 'Site visit records',
  'location.visits.empty': 'No additional visit records have been published for this site.',
  'location.visits.error': 'Visit records could not be loaded.',
  'location.monitoring.title': 'Network monitoring',
  'location.monitoring.hint': 'Reviewed network summaries, collected automatically and shown with their observation time.',
  'location.monitoring.empty': 'No monitoring summary has been published for this site yet.',
  'location.monitoring.error': 'Monitoring summaries could not be loaded.',
  'location.monitoring.source': 'Monitoring',
  'location.monitoring.healthy': 'Healthy',
  'location.monitoring.degraded': 'Degraded',
  'location.monitoring.offline': 'Offline',
  'location.monitoring.unknown': 'Unknown',
  'location.monitoring.complete': 'Complete monitoring coverage',
  'location.monitoring.partial': 'Partial monitoring coverage',
  'location.monitoring.none': 'Monitoring coverage not established',
  'location.monitoring.last_reported': 'Last reported state: {status}',
  'location.monitoring.devices': 'Devices',
  'location.monitoring.online': 'Online',
  'location.monitoring.alerts': 'Active alerts',
  'location.monitoring.unknown_devices': '{n} devices have an unknown state.',
  'location.monitoring.inventory': 'Device inventory',
  'location.monitoring.device': 'Device',
  'location.monitoring.incidents': 'Reported incidents',
  'location.monitoring.incident': 'Network incident',
  'location.tab.documents': 'Documents',
  'location.tab.activity': 'Activity',
  'location.tab.reports': 'Reports',
  'location.availability': 'Availability (24h)',
  'location.availability_samples': '{n} samples',
  'location.availability_none': 'No uptime samples yet',
  'location.availability_latest': '{online} of {total} devices online',
  'location.sla': 'SLA compliance',
  'location.open_tickets': 'Open tickets',
  'location.last_visit': 'Last field visit',
  'location.no_visits': 'No field visits recorded',
  'location.systems': 'Installed systems',
  'location.systems_none': 'No systems recorded for this site yet.',
  'location.systems_none_hint':
    'Installed systems will be listed once they are commissioned in PazPortal.',
  'location.resources': 'Site resources',
  'location.sla_response': 'SLA: {n}h response',
  'location.since': 'Since {date}',
  'location.no_check': 'No health check yet',
  'location.inventory': 'Commissioned inventory',
  'location.inventory_hint': 'Serials and warranties as commissioned. Live status is on the Network tab.',
  'location.no_assets': 'No assets registered',
  'location.asset.device': 'Device',
  'location.asset.make_model': 'Make / model',
  'location.asset.serial': 'Serial',
  'location.asset.ip': 'IP',
  'location.asset.firmware': 'Firmware',
  'location.asset.warranty': 'Warranty',
  'location.depends_on': 'Depends on',
  'location.depended_by': 'Depended upon by',
  'location.network.unifi_title': 'UniFi sites',
  'location.network.unifi_none': 'No UniFi site is mapped to this location yet.',
  'location.network.unifi_none_hint': 'Counts appear once the site is linked in the property directory.',
  'location.network.devices': 'Devices',
  'location.network.online': 'online',
  'location.network.offline': 'offline',
  'location.network.clients': 'Clients',
  'location.network.wifi': 'Wi-Fi',
  'location.network.wired': 'wired',
  'location.network.guest': 'guest',
  'location.network.ssids': 'SSIDs',
  'location.network.wan': 'WAN links',
  'location.network.pending': 'Pending updates',
  'location.network.critical': 'Critical notifications',
  'location.network.timezone': 'Time zone',
  'location.network.gateways': 'Gateways',
  'location.network.auvik_title': 'Auvik devices',
  'location.network.auvik_not_connected': 'Auvik not connected',
  'location.network.auvik_not_connected_hint':
    'Device-level monitoring appears here once Auvik is connected for this site.',
  'location.network.alerts': 'Open alerts',
  'location.network.alerts_none': 'No open alerts for this site.',
  'location.network.interfaces': 'Interfaces',
  'location.network.ssids_title': 'SSIDs',
  'location.network.clients_title': 'Recently observed clients',
  'location.network.aps': 'Access points',
  'location.network.none_ssids': 'No SSIDs synced for this site.',
  'location.network.none_clients': 'No clients observed in the last 15 minutes.',
  'location.network.none_aps': 'No access points synced.',
  'location.network.source_unifi': 'UniFi',
  'location.network.source_auvik': 'Auvik',
  'location.network.enabled': 'enabled',
  'location.network.disabled': 'disabled',
  'location.network.hidden': 'hidden',
  'location.device.device': 'Device',
  'location.device.type': 'Type',
  'location.device.status': 'Status',
  'location.device.availability': 'Availability',
  'location.device.vendor': 'Vendor',
  'location.device.ip': 'IP',
  'location.device.firmware': 'Firmware',
  'location.device.last_seen': 'Last seen',
  'location.activity_empty': 'No activity recorded for this site yet.',
  'location.reports_hint': 'Daily reports delivered by the PazNetworks field team.',
  'location.reports_empty': 'No daily reports yet',
  'location.reports_empty_hint': 'Delivered daily reports appear here.',
  'location.report.day': 'Day {n}',
  'location.report.crew': 'PazNetworks crew',
  'location.report.delivered': 'delivered {ago}',
  'location.report.work': 'Work completed',
  'location.report.plan': 'Plan for next day',
  'location.report.no_content': 'Report has no narrative content.',

  // Documents
  'documents.title': 'Documents',
  'documents.search': 'Search documents',
  'documents.download': 'Download',
  'documents.open': 'Open',
  'documents.opens_in_new_tab': 'Opens the article in PazNet Learn in a new tab',
  'documents.source.zoho_learn': 'PazNet Learn',
  'documents.download_failed': 'Download failed',
  'documents.empty': 'No documents yet',
  'documents.empty_category': 'No {category} yet',
  'documents.empty_hint': 'Documents shared by PazNetworks appear here.',
  'documents.error': 'Could not load documents.',
  'documents.all_locations': 'All locations',
  'documents.category.all': 'All',
  'documents.category.rack_elevation': 'Rack Schedules',
  'documents.category.network_diagram': 'Network diagrams',
  'documents.category.floor_plan': 'Floor plans',
  'documents.category.as_built': 'Install reports',
  'documents.category.photo': 'Photos',
  'documents.category.other': 'Manuals',
  'documents.category.certificate': 'Certificates',
  'documents.category.contract': 'Contracts',
  'documents.category.kb': 'KBs',
  'documents.category.ipam': 'IP Address Management (IPAM)',
  'documents.category.ipam_hint': 'Networks, VLANs, VZones',
  'documents.category.website': 'Websites',
  'documents.category.expiration': 'Expirations',
  'documents.category.application': 'Applications',
  'documents.category.computer_asset': 'Computer Assets',
  'documents.category.location': 'Locations',
  'documents.category.network_device': 'Network Devices',
  'documents.category.person': 'People',
  'documents.category.vendor': 'Vendors',
  'documents.category.wireless': 'Wireless',
  'documents.category.carrier_wan': 'Carrier & WAN',
  'documents.category.telephony': 'Telephony',

  // Billing
  'billing.title': 'Contracts & billing',
  'billing.choose_account': 'Account',
  'billing.no_info': 'No billing information',
  'billing.no_info_hint': 'Contact your account manager for billing details.',
  'billing.annual_value': 'Annual value',
  'billing.per_month': '{amount}/month',
  'billing.no_value': 'No contract value on file',
  'billing.renewal': 'Renewal date',
  'billing.no_renewal': 'No renewal date on file',
  'billing.sla_tier': 'SLA tier',
  'billing.not_set': 'Not set',
  'billing.contract': 'Contract',
  'billing.cycle': 'Billing cycle',
  'billing.account_number': 'Account number',
  'billing.invoices': 'Invoices',
  'billing.invoices_empty': 'No invoices yet. They will appear here when issued.',
  'billing.invoices_error': 'Could not load invoices.',
  'billing.invoice': 'Invoice',
  'billing.date': 'Date',
  'billing.amount': 'Amount',
  'billing.status': 'Status',
  'billing.pay_now': 'Pay now',
  'billing.view': 'View',
  'billing.pdf': 'PDF',
  'billing.footer': 'Contact your account manager to update billing information.',

  // Tickets
  'tickets.title': 'Support tickets',
  'tickets.portfolio_hint': 'Showing tickets across all linked accounts',
  'tickets.new': 'New ticket',
  'tickets.open': 'Open tickets',
  'tickets.resolved': 'Recently resolved',
  'tickets.error': 'Could not load tickets',
  'tickets.empty': 'No open tickets',
  'tickets.empty_hint': 'Create a ticket if you need support.',
  'tickets.no_sla': 'No SLA',
  'tickets.sla_breached': 'SLA breached',
  'tickets.remaining': '{h}h {m}m remaining',
  'tickets.resolved_ago': 'Resolved {ago}',
  'tickets.unassigned': 'Unassigned site',
  'tickets.n_open': '{n} open',
  'tickets.form.title': 'New support ticket',
  'tickets.form.close': 'Close',
  'tickets.form.subject': 'Title',
  'tickets.form.subject_placeholder': 'Brief description of the issue',
  'tickets.form.description': 'Description',
  'tickets.form.description_placeholder':
    'What is affected, since when, and any steps already taken',
  'tickets.form.priority': 'Priority',
  'tickets.form.site': 'Site',
  'tickets.form.site_placeholder': 'Select a site',
  'tickets.form.system': 'System',
  'tickets.form.system_placeholder': 'Select a system',
  'tickets.form.cancel': 'Cancel',
  'tickets.form.submit': 'Create ticket',
  'tickets.form.submitting': 'Creating…',
  'tickets.form.choose_site': 'Choose the site this ticket is for.',
  'tickets.form.no_account': 'No account is selected. Refresh and try again.',
  'tickets.form.failed': 'Could not create the ticket. Try again.',
  'tickets.created': 'Ticket {n} created.',
  'tickets.viewas_blocked': 'Tickets cannot be created while viewing the portal as a customer.',
  'tickets.priority.low': 'Low',
  'tickets.priority.medium': 'Medium',
  'tickets.priority.high': 'High',
  'tickets.priority.critical': 'Critical',
  'tickets.detail.back': 'Back to tickets',
  'tickets.detail.not_found': 'Ticket not found',
  'tickets.detail.not_found_hint': 'It may have been removed or belongs to another account.',
  'tickets.detail.description': 'Description',
  'tickets.detail.context': 'Context',
  'tickets.detail.created': 'Created',
  'tickets.detail.account': 'Account',
  'tickets.detail.location': 'Site',
  'tickets.detail.system': 'System',
  'tickets.detail.last_update': 'Last update',
  'tickets.detail.resolution': 'Resolution',
  'tickets.detail.timeline': 'Status timeline',
  'tickets.detail.timeline_empty': 'No recorded changes yet.',
  'tickets.detail.conversation': 'Conversation',
  'tickets.detail.conversation_empty': 'No messages yet. Write below and your PazNetworks support contact will see it.',
  'tickets.detail.conversation_error': 'The conversation could not be loaded.',
  'tickets.detail.conversation_read_only': 'Read-only while viewing as a customer. Exit the view to reply.',
  'tickets.detail.reply_label': 'Add a message',
  'tickets.detail.reply_placeholder': 'Type your message',
  'tickets.detail.reply_send': 'Send message',
  'tickets.detail.reply_sending': 'Sending…',
  'tickets.detail.reply_error': 'The message could not be sent. Try again.',
  'trip.progress_not_started': 'Trip progress: not yet started',
  'trip.progress_step': 'Trip progress: step {n} of {total} — {stage}',
  'trip.engineer_assigned_one': '1 engineer assigned',
  'trip.engineers_assigned': '{n} engineers assigned',
  'trip.related': 'Related trips',
  'trip.day': 'Day {n}',
  'trip.sales_stage': 'Sales stage: {stage}',
  'tickets.filter_site': 'Site',
  'tickets.filter_all_sites': 'All sites',
  'tickets.detail.attach': 'Attach a file',
  'tickets.detail.attach_remove': 'Remove attachment {name}',
  'tickets.detail.attach_too_large': 'That file is too large. Attachments are limited to 10 MB.',
  'tickets.detail.via': 'via {source}',
  'tickets.detail.sla': 'SLA: {date}',

  // Trends
  'trends.title': 'Trends',
  'trends.subtitle':
    'Measured from network availability samples, resolved tickets, and warranty dates. Live site status is on Locations.',
  'trends.choose_account': 'Account',
  'trends.uptime': 'Network availability',
  'trends.uptime_sub': 'Device online % by month',
  'trends.uptime_empty': 'No samples yet',
  'trends.uptime_empty_hint':
    'Month-over-month availability appears once network polling has written its first samples.',
  'trends.resolution': 'Ticket resolution',
  'trends.resolution_sub': 'Average hours by month',
  'trends.resolution_empty': 'No resolved tickets yet',
  'trends.resolution_empty_hint': 'When tickets are resolved, average time-to-resolution shows here.',
  'trends.refresh': 'Asset refresh',
  'trends.refresh_sub': 'Warranty buckets',
  'trends.refresh_empty': 'No installed assets yet',
  'trends.refresh_empty_hint': 'Once equipment is commissioned, refresh forecasting appears here.',
  'trends.closed': '{n} closed',
  'trends.error': 'Could not load trends.',
  'trends.bucket.expired': 'Expired warranties',
  'trends.bucket.expiring_90d': 'Expiring in 90 days',
  'trends.bucket.expiring_1y': 'Expiring in 1 year',
  'trends.bucket.healthy': 'Covered (1y+)',
  'trends.bucket.no_warranty_data': 'No warranty data',

  // Onboarding
  'onboarding.welcome': 'Welcome to PazPortal',
  'onboarding.welcome_body':
    'One place for the PazNetworks services running across your sites.',
  'onboarding.navigate': 'Find your way around',
  'onboarding.navigate_body': 'The main areas of the portal:',
  'onboarding.ready': 'You’re set',
  'onboarding.ready_body':
    'Your access is limited to the accounts linked to your login. Contact your PazNetworks account manager to change it.',
  'onboarding.skip': 'Skip tour',
  'onboarding.next': 'Next',
  'onboarding.start': 'Open the portal',
  'onboarding.dashboard': 'Network status, open tickets and recent activity across your accounts',
  'onboarding.locations': 'Each site’s network telemetry, systems, documents and field reports',
  'onboarding.trends': 'Monthly availability, ticket resolution time and equipment refresh',
  'onboarding.tickets': 'Create and follow support requests',
  'onboarding.documents': 'Rack schedules, network diagrams and shared records',
  'onboarding.billing': 'Contract terms, SLA tier and invoices per account',
  'onboarding.step': 'Step {n} of {total}',

  // Health
  'health.title': 'Portal status',
  'health.checking': 'Checking…',
  'health.ok': 'Operational',
  'health.degraded': 'Degraded',
  'health.database': 'Database (PostgREST)',
  'health.auth': 'Authentication',
  'health.latency': '{ms} ms',
  'health.failed': 'Failed',
  'health.passed': 'Passed',
  'health.rerun': 'Run again',
  'property.solutions.title': 'PazNetworks solutions deployed',
  'property.trips.title': 'On-site trips',
  'property.trips.earlier': 'Earlier trips',
  'property.trips.subtitle': 'Week, month, and 4-month coverage for on-site visits.',
  'property.trips.calendar': 'Calendar',
  'property.trips.list': 'List',
  'property.trips.week': 'Week',
  'property.trips.month': 'Month',
  'property.trips.four_month': '4 months',
  'property.trips.add': 'Schedule visit',
  'property.trips.add_title': 'Schedule an on-site visit',
  'property.trips.title_label': 'Visit title',
  'property.trips.start': 'Start',
  'property.trips.end': 'End',
  'property.trips.notes': 'Notes',
  'property.trips.save': 'Save visit',
  'property.trips.cancel': 'Cancel',
  'property.trips.today': 'Today',
  'property.trips.planned': 'Planned',
  'property.trips.completed': 'Completed',
  'property.trips.scheduled': 'Scheduled',
  'property.trips.on_site': 'On site',
  'property.trips.location': 'Property',
  'property.trips.empty_day': 'No visits on this day.',
  'property.trips.portfolio_title': 'Portfolio coverage',
  'property.trips.portfolio_subtitle': 'Current and upcoming on-site visits across every Stanford Hotels property.',
  'property.trips.portfolio_history': 'All on-site visits for this property, including previous trips.',
  'property.trips.current': 'On site now',
  'property.trips.upcoming': 'Upcoming',
  'property.trips.previous': 'Previous visits',
  'property.trips.coverage_rail': 'Coverage board',
  'property.trips.empty_coverage': 'No current or upcoming visits in this window.',

  // Freshness
  'freshness.live': 'Live',
  'freshness.live_streaming': 'Live · streaming',
  'freshness.stale': 'Stale — Last Known Good',
  'freshness.offline': 'Offline — Last Known Good',
  'freshness.unknown': 'No data yet',
  'freshness.live_short': 'Live',
  'freshness.stale_short': 'Stale',
  'freshness.offline_short': 'Offline',
  'freshness.unknown_short': 'No data',
  'freshness.relative_ago': '{age} ago',
  'freshness.relative_just_now': 'just now',
  'freshness.relative_never': 'never',

  // Login
  'login.title': 'Sign in to your portal',
  'login.email': 'Email address',
  'login.send_link': 'Send sign-in link',
  'login.help': 'Your access is provisioned by your PazNetworks account manager.',

  // Common
  'common.loading': 'Loading…',
  'common.signed_in_as': 'Signed in as',
  'common.sign_out': 'Sign out',
  'common.retry': 'Try again',
  'common.previous': 'Previous',
  'common.next': 'Next',
  'common.page_of': 'Page {page} of {total}',
  'common.updated': 'Updated',
  'common.close': 'Close',
  'common.none': '—',
  'common.new_tab': 'Opens in a new tab',

  // Added during the production-readiness pass (keys referenced by pages)
  'lang.persist_failed': 'Your language choice could not be saved to your profile. It will apply on this device only.',
  'locations.map_error': 'The map could not be rendered. Use the list below to open a property.',
  'dashboard.renewal_account': 'Next renewal: {name} on {date}',
  'dashboard.health_accounts_scored': '{n} of {total} accounts have enough data to score',
  'health.subtitle': 'Live checks against the portal backend. Nothing here is cached.',
  'health.checked_at': 'Checked at {time}',
  'location.network.unifi_hint': 'Counts come from UniFi Site Manager and refresh every five minutes.',
  'location.network.site': 'Site',
  'location.reports_error': 'Daily reports could not be loaded.',
  'location.activity_error': 'Activity could not be loaded.',
  'staff.picker.eyebrow': 'PazNetworks staff',
  'staff.picker.empty_hint': 'Invite a customer contact from the admin workspace to create the first portal login.',
  'staff.picker.col_user': 'Portal user',
  'staff.picker.col_accounts': 'Accounts',
  'tickets.detail.timeline_error': 'The ticket timeline could not be loaded.',
} as const;

type Key = keyof typeof en;

const es: Record<Key, string> = {
  'nav.dashboard': 'Panel',
  'nav.locations': 'Ubicaciones',
  'nav.trends': 'Tendencias',
  'nav.tickets': 'Tickets',
  'nav.documents': 'Documentos',
  'nav.billing': 'Facturación',
  'nav.network': 'Red',
  'nav.services': 'Servicios',
  'nav.systems': 'Sistemas',
  'nav.reports': 'Informes',
  'nav.activity': 'Actividad',
  'nav.all_locations': 'Todas las ubicaciones',
  'nav.sign_out': 'Cerrar sesión',
  'nav.section.overview': 'Resumen',
  'nav.section.site': 'Sitio',
  'nav.section.support': 'Soporte',
  'nav.section.account': 'Cuenta',
  'nav.badge_open': 'abiertos',

  // Centro de notificaciones
  'notifications.title': 'Notificaciones',
  'notifications.empty': 'Nada nuevo. Las novedades de sus tickets, visitas y documentos aparecen aquí.',
  'notifications.mark_all_read': 'Marcar todo como leído',
  'notifications.unread': 'Sin leer',
  'notifications.aria_none': 'Notificaciones',
  'notifications.aria_unread': 'Notificaciones, {n} sin leer',

  // Controles del calendario
  // Manuales del sitio (Zoho Learn)
  'location.manuals.title': 'Documentación del sitio',
  'location.manuals.subtitle': 'Los manuales que PazNetworks mantiene para esta propiedad en PazNet Learn.',
  'location.manuals.property': 'Manual de la propiedad',
  'location.manuals.property_hint': 'Diseño de red, sistemas, procedimientos vigentes y contactos del sitio.',
  'location.manuals.engineering': 'Manual de ingeniería e instalaciones',
  'location.manuals.engineering_hint': 'Detalle de planta, ductos e instalaciones de esta propiedad.',
  'location.manuals.opens_in_learn': 'Se abre en PazNet Learn en una pestaña nueva',

  // Paleta de comandos
  'palette.title': 'Búsqueda y comandos',
  'palette.placeholder': 'Busque sitios, tickets y páginas…',
  'palette.results': 'Resultados',
  'palette.empty': 'No hay coincidencias.',
  'palette.footer': 'Flechas para moverse · Enter para abrir · Esc para cerrar',
  'palette.kind_page': 'Página',
  'palette.kind_site': 'Sitio',
  'palette.kind_ticket': 'Ticket',
  'palette.kind_action': 'Acción',
  'palette.action_new_ticket': 'Abrir un ticket de soporte',
  'palette.action_trends': 'Ver tendencias del portafolio',

  'documents.filter_label': 'Filtrar documentos por categoría',
  'documents.filter_all_sites': 'Todos los sitios',

  // Diseño del panel
  'dashboard.customise': 'Personalizar',
  'dashboard.customise_title': 'Secciones del panel',
  'dashboard.customise_hidden': '{n} ocultas',
  'dashboard.customise_reset': 'Restablecer',
  'dashboard.customise_hint': 'Se guarda en su cuenta, en todos los dispositivos donde inicie sesión.',
  'dashboard.customise_move_up': 'Mover {name} hacia arriba',
  'dashboard.customise_move_down': 'Mover {name} hacia abajo',
  'dashboard.widget.metrics': 'Tarjetas de resumen',
  'dashboard.widget.sla': 'Cumplimiento del SLA',
  'dashboard.widget.trips': 'Calendario de visitas',

  // Centro de control de la propiedad
  'location.availability_trend': 'Disponibilidad, últimos 30 días',
  'location.availability_trend_empty': 'Aún no hay muestras suficientes para una tendencia.',
  'location.availability_trend_point_dip': '{date} · bajó a {worst}%',
  'location.last_visit_on': 'Última visita {date}',
  'location.timeline.title': 'Historial del sitio',
  'location.timeline.subtitle': 'Lo que entregamos aquí y lo que está programado.',
  'location.timeline.delivered': 'Entregado',
  'location.timeline.scheduled': 'Programado',
  'location.timeline.on_site': 'En sitio ahora',
  'location.timeline.empty': 'Aún no hay visitas registradas para este sitio.',

  // Vista de mando del portafolio
  'dashboard.sites_title': 'Estado del portafolio',
  'dashboard.sites_subtitle': 'Tiempo en línea de la WAN de cada red. Seleccione un sitio para abrirlo.',
  'dashboard.sites_devices': '{online}/{total} en línea',
  'dashboard.sites_live': 'En línea',
  'dashboard.sites_down': 'Caído',
  'dashboard.sites_uptime': '{pct} de disponibilidad',
  'dashboard.sites_no_telemetry': 'Sin monitoreo',
  'dashboard.sites_telemetry_lost': 'Monitoreo fuera de línea · visto por última vez hace {age}',
  'dashboard.sites_legend': 'Estado del sitio:',
  'dashboard.sites_state_good': 'En línea',
  'dashboard.sites_state_warning': 'Requiere atención',
  'dashboard.sites_state_serious': 'Degradado',
  'dashboard.sites_state_critical': 'Caído',
  'dashboard.sites_state_unknown': 'Sin monitoreo',
  'dashboard.exceptions_title': 'Redes caídas',
  'dashboard.exceptions_none': 'Todas las redes monitoreadas están en línea.',
  'dashboard.exceptions_tickets': '{n} abiertos',
  'dashboard.systems_state_operational': 'Operativo',
  'dashboard.systems_state_degraded': 'Degradado',
  'dashboard.systems_state_offline': 'Fuera de línea',
  'calendar.previous': 'Período anterior',
  'calendar.next': 'Período siguiente',
  'calendar.remove': 'Quitar',
  'calendar.visits_in_window_one': '1 visita en este período',
  'calendar.visits_in_window': '{n} visitas en este período',
  'property.trips.select_property': 'Seleccione una propiedad…',
  'property.trips.title_placeholder': 'p. ej. cobertura del ACC Tip-Off',
  'property.trips.notes_placeholder': 'Notas de cobertura para PazNetworks',

  'shell.all_accounts': 'Todas las cuentas',
  'shell.locations_count': '{n} ubicaciones',
  'shell.location_count_one': '1 ubicación',
  'shell.loading': 'Cargando…',

  'lang.label': 'Idioma',
  'lang.en': 'English',
  'lang.es': 'Español',

  'account.label': 'Cuenta',
  'account.all': 'Todas las cuentas',
  'account.all_count': 'Todas las cuentas ({n})',
  'account.portfolio_hint': 'Mostrando todas las cuentas vinculadas',
  'account.none': 'No hay una cuenta de cliente asignada',
  'access.invitations': 'Invitaciones a cuentas de cliente',
  'access.invitation_hint': 'Acepta una invitación para acceder a esa cuenta de cliente.',
  'access.accept': 'Aceptar invitación',
  'access.accept_for': 'Aceptar invitación para {name}',
  'access.accepting': 'Aceptando…',
  'access.accept_failed': 'No se pudo aceptar esta invitación. Puede haber cambiado o haberse revocado. Actualiza e inténtalo de nuevo.',
  'access.unavailable': 'No pudimos verificar tu acceso actual a las cuentas. Actualiza para intentarlo de nuevo.',
  'access.refresh': 'Actualizar acceso',
  'access.loading': 'Verificando el acceso a las cuentas…',
  'account.none_hint':
    'Pida a un administrador de PazNetworks que vincule este acceso a una cuenta de cliente.',
  'account.error': 'No se pudieron cargar sus cuentas.',

  'viewas.viewing_as': 'Viendo como',
  'viewas.accounts': '{n} cuentas',
  'viewas.account_one': '1 cuenta',
  'viewas.exit': 'Salir de la vista',
  'viewas.loading': 'Cargando usuario del portal…',
  'viewas.audit_failed': 'No se pudo registrar la entrada de auditoría',
  'viewas.read_only':
    'Solo lectura: las acciones están deshabilitadas mientras se ve el portal como cliente.',
  'viewas.region': 'Aviso de vista como cliente',

  'staff.picker.title': 'Elija un usuario del portal',
  'staff.picker.subtitle':
    'Abra el portal exactamente como lo ve uno de sus clientes. Cada página se limita a las cuentas vinculadas al usuario elegido y la sesión queda registrada en la auditoría.',
  'staff.picker.search': 'Buscar por nombre, correo o cuenta',
  'staff.picker.no_results': 'Ningún usuario del portal coincide con la búsqueda.',
  'staff.picker.empty': 'Todavía no hay usuarios del portal con cuentas vinculadas.',
  'staff.picker.error': 'No se pudieron cargar los usuarios del portal.',
  'staff.picker.last_login': 'Último inicio de sesión',
  'staff.picker.never': 'Nunca',
  'staff.picker.view': 'Ver portal',
  'staff.picker.back_admin': 'Volver a administración',
  'staff.picker.not_found': 'No se encontró ese usuario del portal. Elija otro.',
  'staff.picker.more': '+{n} más',
  'staff.picker.count': '{n} usuarios del portal',
  'staff.picker.count_one': '1 usuario del portal',

  'dashboard.locations': 'Ubicaciones',
  'dashboard.locations_mapped': '{n} con telemetría de red',
  'dashboard.network': 'Red',
  'dashboard.network_devices_online': '{online} de {total} dispositivos en línea',
  'dashboard.network_sites_live': '{live} de {total} en línea',
  'dashboard.network_sites_down': '{n} caídas',
  'dashboard.network_all_live': 'Todas las redes monitoreadas están en línea',
  'dashboard.network_offline': '{n} sin conexión',
  'dashboard.network_clients': '{n} clientes conectados',
  'dashboard.network_ssids': '{n} SSID',
  'dashboard.network_sites': '{n} sitios UniFi',
  'dashboard.network_none': 'Aún sin telemetría de red',
  'dashboard.network_none_hint': 'Los sitios UniFi todavía no están vinculados a estas ubicaciones.',
  'dashboard.network_source': 'UniFi',
  'dashboard.open_tickets': 'Tickets abiertos',
  'dashboard.open_tickets_hint': 'En las cuentas seleccionadas',
  'dashboard.renewal': 'Renovación del contrato',
  'dashboard.renewal_days': '{n} días',
  'dashboard.renewal_none': 'No hay fecha de renovación registrada',
  'dashboard.renewal_past': 'La fecha de renovación ya pasó',
  'dashboard.alerts': 'Alertas de red',
  'dashboard.alerts_not_connected': 'Auvik no está conectado',
  'dashboard.alerts_none': 'Sin alertas abiertas',
  'dashboard.alerts_open': 'Alertas abiertas',
  'dashboard.operational_health': 'Salud operativa',
  'dashboard.operational_health_subtitle':
    'Calculada a partir de tickets, plazos de SLA y disponibilidad de red.',
  'dashboard.health_not_enough': 'Datos insuficientes',
  'dashboard.health_not_enough_hint':
    'La puntuación aparece cuando se han recopilado tickets y telemetría de red para esta cuenta.',
  'dashboard.health_of_100': 'de 100',
  'dashboard.health_computed': 'Calculada {ago}',
  'dashboard.uptime': 'Disponibilidad de red',
  'dashboard.tickets': 'Tickets',
  'dashboard.sla': 'SLA',
  'dashboard.sla_compliance': 'Cumplimiento del SLA',
  'dashboard.sla_met': '{met} de {total} plazos cumplidos',
  'dashboard.sla_none': 'Todavía no hay tickets con plazo de SLA',
  'dashboard.sla_tier': 'Nivel de SLA',
  'dashboard.installed_platforms': 'Sistemas instalados',
  'dashboard.active_field_project': 'Proyecto en sitio activo',
  'dashboard.recent_activity': 'Actividad reciente',
  'dashboard.activity_empty': 'Sin actividad reciente',
  'dashboard.activity_empty_hint':
    'Aquí aparecerán las actualizaciones de tickets, visitas técnicas y documentos compartidos.',
  'dashboard.activity_error': 'No se pudo cargar la actividad reciente.',
  'dashboard.error': 'No se pudo cargar su panel.',
  'dashboard.offline_since': 'sin conexión desde',
  'dashboard.offline': 'sin conexión',
  'dashboard.dismiss': 'Descartar',
  'dashboard.actor.system': 'Sistema',
  'dashboard.actor.paznetworks': 'PazNetworks',
  'dashboard.actor.customer': 'Su equipo',

  'locations.title': 'Ubicaciones',
  'locations.subtitle_count': '{n} sitios',
  'locations.subtitle_count_one': '1 sitio',
  'locations.for_account': 'de {name}',
  'locations.map_title': 'Mapa de propiedades',
  'locations.map_subtitle': 'Seleccione un marcador para abrir los recursos del sitio.',
  'locations.map_choose': 'Elija un sitio del mapa',
  'locations.map_unavailable': 'Mapa interactivo no disponible',
  'locations.map_unavailable_hint':
    'Este navegador no puede renderizar WebGL. Los sitios con coordenadas se muestran como lista.',
  'locations.list_title': 'Sitios',
  'locations.list_subtitle': 'Tickets, telemetría de red y documentos de cada sitio.',
  'locations.search': 'Buscar sitios',
  'locations.no_match': 'Ningún sitio coincide con “{q}”.',
  'locations.error_title': 'No se pudieron cargar las ubicaciones',
  'locations.error_subtitle': 'Actualice la página o contacte al soporte de PazNetworks.',
  'locations.empty.title': 'No hay ubicaciones configuradas todavía',
  'locations.empty.subtitle':
    'Su gerente de cuenta de PazNetworks agregará sus sitios durante la incorporación.',
  'locations.card.type': 'Tipo',
  'locations.card.network': 'Red',
  'locations.card.open_tickets': 'Tickets abiertos',
  'locations.card.timezone': 'Zona horaria',
  'locations.card.no_telemetry': 'Sin monitoreo',
  'locations.card.live': 'En línea',
  'locations.card.down': 'Caído',
  'locations.card.devices': '{online}/{total} en línea',
  'locations.address_missing': 'Dirección no registrada',
  'locations.open_site': 'Abrir página del sitio',

  'property.resources.site': 'Recursos del sitio',
  'property.resources.close': 'Cerrar detalles del sitio',
  'property.resources.tickets': 'Tickets de soporte',
  'property.resources.new_ticket': 'Nuevo ticket',
  'property.resources.no_open_tickets': 'No hay tickets abiertos para este sitio.',
  'property.resources.view_all_tickets': 'Ver todos los tickets',
  'property.resources.network_title': 'Red UniFi',
  'property.resources.network_none': 'Aún no hay telemetría UniFi para este sitio.',
  'property.resources.devices_online': 'dispositivos en línea',
  'property.resources.clients': 'clientes',
  'property.resources.ssids': 'SSID',
  'property.resources.operations': 'Red y operaciones',
  'property.resources.mission_control': 'Ver el estado de la red de este sitio en el portal',
  'property.resources.auvik': 'Abrir este sitio en Auvik (monitoreo de red)',
  'property.resources.unifi': 'Abrir la consola UniFi de este sitio',
  'property.resources.vendor_website': 'Proveedor de comunicaciones · sitio web público',
  'property.resources.additional_networks': 'Redes adicionales',
  'property.resources.guest_experience': 'Experiencia del huésped',
  'property.resources.rankings_title': 'Rankings',
  'property.resources.rankings': 'Posiciones, tendencias e historial del hotel',
  'property.resources.ordering': 'Menú de pedidos para huéspedes',
  'property.resources.knowledge': 'Conocimiento y registros del sitio',
  'property.resources.external_learn': 'Manual compartido del sitio · puede requerir contraseña',
  'property.resources.engineering': 'Ingeniería e instalaciones',
  'property.resources.engineering_description': 'Manual de ingeniería del sitio',
  'property.resources.missing_links': 'Enlaces sin configurar',
  'property.resources.not_linked': 'Todavía no hay un enlace registrado para este sitio.',
  'property.resources.no_directory': 'Este sitio aún no está vinculado al directorio de propiedades.',
  'property.resources.new_tab': '(se abre en una pestaña nueva)',
  'property.resources.sign_in':
    'Los enlaces se abren en una pestaña nueva. Algunos servicios requieren iniciar sesión o una contraseña de acceso.',

  'location.back': 'Volver a ubicaciones',
  'location.not_found': 'Ubicación no encontrada',
  'location.not_found_hint': 'Puede que se haya eliminado o pertenezca a otra cuenta.',
  'location.tab.overview': 'Resumen',
  'location.tab.systems': 'Sistemas',
  'location.tab.network': 'Red',
  'location.tab.services': 'Servicios',
  'location.services.title': 'Circuitos y servicios',
  'location.services.hint': 'Información revisada de conectividad y proveedores para este sitio.',
  'location.services.empty': 'Todavía no se han publicado servicios para este sitio.',
  'location.services.error': 'No se pudieron cargar los servicios.',
  'location.services.service': 'Servicio',
  'location.services.circuit': 'ID del circuito',
  'location.services.bandwidth': 'Ancho de banda',
  'location.services.transport': 'Transporte / entrega',
  'location.services.sla': 'Compromiso de servicio',
  'location.services.restoration': 'Objetivo de restauración: {n} horas',
  'location.visits.title': 'Registro de visitas',
  'location.visits.empty': 'No hay visitas adicionales publicadas para este sitio.',
  'location.visits.error': 'No se pudo cargar el registro de visitas.',
  'location.monitoring.title': 'Monitoreo de red',
  'location.monitoring.hint': 'Resúmenes de red revisados, recopilados automáticamente y con la hora de observación.',
  'location.monitoring.empty': 'Todavía no se ha publicado un resumen de monitoreo para este sitio.',
  'location.monitoring.error': 'No se pudieron cargar los resúmenes de monitoreo.',
  'location.monitoring.source': 'Monitoreo',
  'location.monitoring.healthy': 'Saludable',
  'location.monitoring.degraded': 'Degradada',
  'location.monitoring.offline': 'Sin conexión',
  'location.monitoring.unknown': 'Desconocido',
  'location.monitoring.complete': 'Cobertura de monitoreo completa',
  'location.monitoring.partial': 'Cobertura de monitoreo parcial',
  'location.monitoring.none': 'Cobertura de monitoreo sin establecer',
  'location.monitoring.last_reported': 'Último estado informado: {status}',
  'location.monitoring.devices': 'Dispositivos',
  'location.monitoring.online': 'En línea',
  'location.monitoring.alerts': 'Alertas activas',
  'location.monitoring.unknown_devices': '{n} dispositivos tienen estado desconocido.',
  'location.monitoring.inventory': 'Inventario de dispositivos',
  'location.monitoring.device': 'Dispositivo',
  'location.monitoring.incidents': 'Incidentes informados',
  'location.monitoring.incident': 'Incidente de red',
  'location.tab.documents': 'Documentos',
  'location.tab.activity': 'Actividad',
  'location.tab.reports': 'Informes',
  'location.availability': 'Disponibilidad (24 h)',
  'location.availability_samples': '{n} muestras',
  'location.availability_none': 'Aún sin muestras de disponibilidad',
  'location.availability_latest': '{online} de {total} dispositivos en línea',
  'location.sla': 'Cumplimiento del SLA',
  'location.open_tickets': 'Tickets abiertos',
  'location.last_visit': 'Última visita técnica',
  'location.no_visits': 'Sin visitas técnicas registradas',
  'location.systems': 'Sistemas instalados',
  'location.systems_none': 'Todavía no hay sistemas registrados para este sitio.',
  'location.systems_none_hint':
    'Los sistemas instalados aparecerán cuando se den de alta en PazPortal.',
  'location.resources': 'Recursos del sitio',
  'location.sla_response': 'SLA: respuesta en {n} h',
  'location.since': 'Desde {date}',
  'location.no_check': 'Sin verificación todavía',
  'location.inventory': 'Inventario instalado',
  'location.inventory_hint':
    'Series y garantías según la puesta en marcha. El estado en vivo está en la pestaña Red.',
  'location.no_assets': 'Sin activos registrados',
  'location.asset.device': 'Dispositivo',
  'location.asset.make_model': 'Marca / modelo',
  'location.asset.serial': 'Serie',
  'location.asset.ip': 'IP',
  'location.asset.firmware': 'Firmware',
  'location.asset.warranty': 'Garantía',
  'location.depends_on': 'Depende de',
  'location.depended_by': 'Requerido por',
  'location.network.unifi_title': 'Sitios UniFi',
  'location.network.unifi_none': 'Ningún sitio UniFi está vinculado a esta ubicación todavía.',
  'location.network.unifi_none_hint':
    'Los conteos aparecen cuando el sitio se vincula en el directorio de propiedades.',
  'location.network.devices': 'Dispositivos',
  'location.network.online': 'en línea',
  'location.network.offline': 'sin conexión',
  'location.network.clients': 'Clientes',
  'location.network.wifi': 'Wi-Fi',
  'location.network.wired': 'cableados',
  'location.network.guest': 'invitados',
  'location.network.ssids': 'SSID',
  'location.network.wan': 'Enlaces WAN',
  'location.network.pending': 'Actualizaciones pendientes',
  'location.network.critical': 'Notificaciones críticas',
  'location.network.timezone': 'Zona horaria',
  'location.network.gateways': 'Gateways',
  'location.network.auvik_title': 'Dispositivos Auvik',
  'location.network.auvik_not_connected': 'Auvik no está conectado',
  'location.network.auvik_not_connected_hint':
    'El monitoreo por dispositivo aparecerá aquí cuando Auvik esté conectado para este sitio.',
  'location.network.alerts': 'Alertas abiertas',
  'location.network.alerts_none': 'No hay alertas abiertas para este sitio.',
  'location.network.interfaces': 'Interfaces',
  'location.network.ssids_title': 'SSID',
  'location.network.clients_title': 'Clientes observados recientemente',
  'location.network.aps': 'Puntos de acceso',
  'location.network.none_ssids': 'No hay SSID sincronizados para este sitio.',
  'location.network.none_clients': 'No se observaron clientes en los últimos 15 minutos.',
  'location.network.none_aps': 'No hay puntos de acceso sincronizados.',
  'location.network.source_unifi': 'UniFi',
  'location.network.source_auvik': 'Auvik',
  'location.network.enabled': 'habilitado',
  'location.network.disabled': 'deshabilitado',
  'location.network.hidden': 'oculto',
  'location.device.device': 'Dispositivo',
  'location.device.type': 'Tipo',
  'location.device.status': 'Estado',
  'location.device.availability': 'Disponibilidad',
  'location.device.vendor': 'Fabricante',
  'location.device.ip': 'IP',
  'location.device.firmware': 'Firmware',
  'location.device.last_seen': 'Visto por última vez',
  'location.activity_empty': 'Todavía no hay actividad registrada para este sitio.',
  'location.reports_hint': 'Informes diarios entregados por el equipo de campo de PazNetworks.',
  'location.reports_empty': 'Aún no hay informes diarios',
  'location.reports_empty_hint': 'Los informes diarios entregados aparecen aquí.',
  'location.report.day': 'Día {n}',
  'location.report.crew': 'Equipo de PazNetworks',
  'location.report.delivered': 'entregado {ago}',
  'location.report.work': 'Trabajo realizado',
  'location.report.plan': 'Plan para el día siguiente',
  'location.report.no_content': 'El informe no tiene contenido narrativo.',

  'documents.title': 'Documentos',
  'documents.search': 'Buscar documentos',
  'documents.download': 'Descargar',
  'documents.open': 'Abrir',
  'documents.opens_in_new_tab': 'Abre el artículo en PazNet Learn en una pestaña nueva',
  'documents.source.zoho_learn': 'PazNet Learn',
  'documents.download_failed': 'Falló la descarga',
  'documents.empty': 'Aún no hay documentos',
  'documents.empty_category': 'Aún no hay {category}',
  'documents.empty_hint': 'Los documentos compartidos por PazNetworks aparecen aquí.',
  'documents.error': 'No se pudieron cargar los documentos.',
  'documents.all_locations': 'Todas las ubicaciones',
  'documents.category.all': 'Todos',
  'documents.category.rack_elevation': 'Esquemas de rack',
  'documents.category.network_diagram': 'Diagramas de red',
  'documents.category.floor_plan': 'Planos',
  'documents.category.as_built': 'Informes de instalación',
  'documents.category.photo': 'Fotos',
  'documents.category.other': 'Manuales',
  'documents.category.certificate': 'Certificados',
  'documents.category.contract': 'Contratos',
  'documents.category.kb': 'Base de conocimiento',
  'documents.category.ipam': 'Gestión de direcciones IP (IPAM)',
  'documents.category.ipam_hint': 'Redes, VLANs, VZones',
  'documents.category.website': 'Sitios web',
  'documents.category.expiration': 'Vencimientos',
  'documents.category.application': 'Aplicaciones',
  'documents.category.computer_asset': 'Equipos informáticos',
  'documents.category.location': 'Ubicaciones',
  'documents.category.network_device': 'Dispositivos de red',
  'documents.category.person': 'Personas',
  'documents.category.vendor': 'Proveedores',
  'documents.category.wireless': 'Inalámbrico',
  'documents.category.carrier_wan': 'Operadores y WAN',
  'documents.category.telephony': 'Telefonía',

  'billing.title': 'Contratos y facturación',
  'billing.choose_account': 'Cuenta',
  'billing.no_info': 'Sin información de facturación',
  'billing.no_info_hint': 'Contacte a su gerente de cuenta para conocer los detalles de facturación.',
  'billing.annual_value': 'Valor anual',
  'billing.per_month': '{amount}/mes',
  'billing.no_value': 'No hay valor de contrato registrado',
  'billing.renewal': 'Fecha de renovación',
  'billing.no_renewal': 'No hay fecha de renovación registrada',
  'billing.sla_tier': 'Nivel de SLA',
  'billing.not_set': 'Sin definir',
  'billing.contract': 'Contrato',
  'billing.cycle': 'Ciclo de facturación',
  'billing.account_number': 'Número de cuenta',
  'billing.invoices': 'Facturas',
  'billing.invoices_empty': 'Aún no hay facturas. Aparecerán aquí cuando se emitan.',
  'billing.invoices_error': 'No se pudieron cargar las facturas.',
  'billing.invoice': 'Factura',
  'billing.date': 'Fecha',
  'billing.amount': 'Importe',
  'billing.status': 'Estado',
  'billing.pay_now': 'Pagar ahora',
  'billing.view': 'Ver',
  'billing.pdf': 'PDF',
  'billing.footer': 'Contacte a su gerente de cuenta para actualizar la información de facturación.',

  'tickets.title': 'Tickets de soporte',
  'tickets.portfolio_hint': 'Mostrando tickets de todas las cuentas vinculadas',
  'tickets.new': 'Nuevo ticket',
  'tickets.open': 'Tickets abiertos',
  'tickets.resolved': 'Resueltos recientemente',
  'tickets.error': 'No se pudieron cargar los tickets',
  'tickets.empty': 'No hay tickets abiertos',
  'tickets.empty_hint': 'Cree un ticket si necesita soporte.',
  'tickets.no_sla': 'Sin SLA',
  'tickets.sla_breached': 'SLA incumplido',
  'tickets.remaining': '{h} h {m} min restantes',
  'tickets.resolved_ago': 'Resuelto {ago}',
  'tickets.unassigned': 'Sitio sin asignar',
  'tickets.n_open': '{n} abiertos',
  'tickets.form.title': 'Nuevo ticket de soporte',
  'tickets.form.close': 'Cerrar',
  'tickets.form.subject': 'Título',
  'tickets.form.subject_placeholder': 'Breve descripción del problema',
  'tickets.form.description': 'Descripción',
  'tickets.form.description_placeholder':
    'Qué está afectado, desde cuándo y qué pasos ya se intentaron',
  'tickets.form.priority': 'Prioridad',
  'tickets.form.site': 'Sitio',
  'tickets.form.site_placeholder': 'Seleccione un sitio',
  'tickets.form.system': 'Sistema',
  'tickets.form.system_placeholder': 'Seleccione un sistema',
  'tickets.form.cancel': 'Cancelar',
  'tickets.form.submit': 'Crear ticket',
  'tickets.form.submitting': 'Creando…',
  'tickets.form.choose_site': 'Elija el sitio al que corresponde este ticket.',
  'tickets.form.no_account': 'No hay una cuenta seleccionada. Actualice e intente de nuevo.',
  'tickets.form.failed': 'No se pudo crear el ticket. Intente de nuevo.',
  'tickets.created': 'Ticket {n} creado.',
  'tickets.viewas_blocked': 'No se pueden crear tickets mientras se ve el portal como cliente.',
  'tickets.priority.low': 'Baja',
  'tickets.priority.medium': 'Media',
  'tickets.priority.high': 'Alta',
  'tickets.priority.critical': 'Crítica',
  'tickets.detail.back': 'Volver a tickets',
  'tickets.detail.not_found': 'Ticket no encontrado',
  'tickets.detail.not_found_hint': 'Puede que se haya eliminado o pertenezca a otra cuenta.',
  'tickets.detail.description': 'Descripción',
  'tickets.detail.context': 'Contexto',
  'tickets.detail.created': 'Creado',
  'tickets.detail.account': 'Cuenta',
  'tickets.detail.location': 'Sitio',
  'tickets.detail.system': 'Sistema',
  'tickets.detail.last_update': 'Última actualización',
  'tickets.detail.resolution': 'Resolución',
  'tickets.detail.timeline': 'Historial de estado',
  'tickets.detail.timeline_empty': 'Aún no hay cambios registrados.',
  'tickets.detail.conversation': 'Conversación',
  'tickets.detail.conversation_empty': 'Aún no hay mensajes. Escriba abajo y su contacto de soporte de PazNetworks lo verá.',
  'tickets.detail.conversation_error': 'No se pudo cargar la conversación.',
  'tickets.detail.conversation_read_only': 'Solo lectura mientras ve el portal como cliente. Salga de la vista para responder.',
  'tickets.detail.reply_label': 'Agregar un mensaje',
  'tickets.detail.reply_placeholder': 'Escriba su mensaje',
  'tickets.detail.reply_send': 'Enviar mensaje',
  'tickets.detail.reply_sending': 'Enviando…',
  'tickets.detail.reply_error': 'No se pudo enviar el mensaje. Inténtelo de nuevo.',
  'trip.progress_not_started': 'Progreso del viaje: aún no ha comenzado',
  'trip.progress_step': 'Progreso del viaje: paso {n} de {total} — {stage}',
  'trip.engineer_assigned_one': '1 ingeniero asignado',
  'trip.engineers_assigned': '{n} ingenieros asignados',
  'trip.related': 'Viajes relacionados',
  'trip.day': 'Día {n}',
  'trip.sales_stage': 'Etapa de venta: {stage}',
  'tickets.filter_site': 'Sitio',
  'tickets.filter_all_sites': 'Todos los sitios',
  'tickets.detail.attach': 'Adjuntar un archivo',
  'tickets.detail.attach_remove': 'Quitar el adjunto {name}',
  'tickets.detail.attach_too_large': 'Ese archivo es demasiado grande. Los adjuntos se limitan a 10 MB.',
  'tickets.detail.via': 'vía {source}',
  'tickets.detail.sla': 'SLA: {date}',

  'trends.title': 'Tendencias',
  'trends.subtitle':
    'Medidas a partir de muestras de disponibilidad de red, tickets resueltos y fechas de garantía. El estado en vivo está en Ubicaciones.',
  'trends.choose_account': 'Cuenta',
  'trends.uptime': 'Disponibilidad de red',
  'trends.uptime_sub': '% de dispositivos en línea por mes',
  'trends.uptime_empty': 'Aún sin muestras',
  'trends.uptime_empty_hint':
    'La disponibilidad mensual aparece cuando el monitoreo de red registra sus primeras muestras.',
  'trends.resolution': 'Resolución de tickets',
  'trends.resolution_sub': 'Horas promedio por mes',
  'trends.resolution_empty': 'Aún no hay tickets resueltos',
  'trends.resolution_empty_hint':
    'Cuando se resuelvan tickets, el tiempo promedio de resolución aparecerá aquí.',
  'trends.refresh': 'Renovación de activos',
  'trends.refresh_sub': 'Grupos por garantía',
  'trends.refresh_empty': 'Aún no hay activos instalados',
  'trends.refresh_empty_hint':
    'Cuando se ponga en marcha el equipamiento, la previsión de renovación aparecerá aquí.',
  'trends.closed': '{n} cerrados',
  'trends.error': 'No se pudieron cargar las tendencias.',
  'trends.bucket.expired': 'Garantías vencidas',
  'trends.bucket.expiring_90d': 'Vencen en 90 días',
  'trends.bucket.expiring_1y': 'Vencen en 1 año',
  'trends.bucket.healthy': 'Cubiertos (más de 1 año)',
  'trends.bucket.no_warranty_data': 'Sin datos de garantía',

  'onboarding.welcome': 'Bienvenido a PazPortal',
  'onboarding.welcome_body':
    'Un solo lugar para los servicios de PazNetworks que operan en sus sitios.',
  'onboarding.navigate': 'Cómo orientarse',
  'onboarding.navigate_body': 'Las áreas principales del portal:',
  'onboarding.ready': 'Todo listo',
  'onboarding.ready_body':
    'Su acceso se limita a las cuentas vinculadas a su usuario. Contacte a su gerente de cuenta de PazNetworks para cambiarlo.',
  'onboarding.skip': 'Omitir recorrido',
  'onboarding.next': 'Siguiente',
  'onboarding.start': 'Abrir el portal',
  'onboarding.dashboard': 'Estado de red, tickets abiertos y actividad reciente de sus cuentas',
  'onboarding.locations': 'Telemetría de red, sistemas, documentos e informes de campo de cada sitio',
  'onboarding.trends': 'Disponibilidad mensual, tiempo de resolución y renovación de equipos',
  'onboarding.tickets': 'Cree y siga solicitudes de soporte',
  'onboarding.documents': 'Esquemas de rack, diagramas de red y registros compartidos',
  'onboarding.billing': 'Condiciones del contrato, nivel de SLA y facturas por cuenta',
  'onboarding.step': 'Paso {n} de {total}',

  'health.title': 'Estado del portal',
  'health.checking': 'Comprobando…',
  'health.ok': 'Operativo',
  'health.degraded': 'Degradado',
  'health.database': 'Base de datos (PostgREST)',
  'health.auth': 'Autenticación',
  'health.latency': '{ms} ms',
  'health.failed': 'Falló',
  'health.passed': 'Correcto',
  'health.rerun': 'Comprobar de nuevo',
  'property.solutions.title': 'Soluciones PazNetworks desplegadas',
  'property.trips.title': 'Viajes en sitio',
  'property.trips.earlier': 'Viajes anteriores',
  'property.trips.subtitle': 'Cobertura semanal, mensual y de 4 meses para visitas en sitio.',
  'property.trips.calendar': 'Calendario',
  'property.trips.list': 'Lista',
  'property.trips.week': 'Semana',
  'property.trips.month': 'Mes',
  'property.trips.four_month': '4 meses',
  'property.trips.add': 'Programar visita',
  'property.trips.add_title': 'Programar una visita en sitio',
  'property.trips.title_label': 'Título de la visita',
  'property.trips.start': 'Inicio',
  'property.trips.end': 'Fin',
  'property.trips.notes': 'Notas',
  'property.trips.save': 'Guardar visita',
  'property.trips.cancel': 'Cancelar',
  'property.trips.today': 'Hoy',
  'property.trips.planned': 'Planificada',
  'property.trips.completed': 'Completada',
  'property.trips.scheduled': 'Programada',
  'property.trips.on_site': 'En sitio',
  'property.trips.location': 'Propiedad',
  'property.trips.empty_day': 'No hay visitas este día.',
  'property.trips.portfolio_title': 'Cobertura del portafolio',
  'property.trips.portfolio_subtitle': 'Visitas actuales y próximas en todas las propiedades de Stanford Hotels.',
  'property.trips.portfolio_history': 'Todas las visitas en sitio de esta propiedad, incluidas las anteriores.',
  'property.trips.current': 'En sitio ahora',
  'property.trips.upcoming': 'Próximas',
  'property.trips.previous': 'Visitas anteriores',
  'property.trips.coverage_rail': 'Tablero de cobertura',
  'property.trips.empty_coverage': 'No hay visitas actuales o próximas en esta ventana.',

  'freshness.live': 'En vivo',
  'freshness.live_streaming': 'En vivo · transmitiendo',
  'freshness.stale': 'Desactualizado — Último estado conocido',
  'freshness.offline': 'Sin conexión — Último estado conocido',
  'freshness.unknown': 'Aún sin datos',
  'freshness.live_short': 'En vivo',
  'freshness.stale_short': 'Desactualizado',
  'freshness.offline_short': 'Sin conexión',
  'freshness.unknown_short': 'Sin datos',
  'freshness.relative_ago': 'hace {age}',
  'freshness.relative_just_now': 'justo ahora',
  'freshness.relative_never': 'nunca',

  'login.title': 'Inicie sesión en su portal',
  'login.email': 'Correo electrónico',
  'login.send_link': 'Enviar enlace de inicio de sesión',
  'login.help': 'Su gerente de cuenta de PazNetworks aprovisiona su acceso.',

  'common.loading': 'Cargando…',
  'common.signed_in_as': 'Sesión iniciada como',
  'common.sign_out': 'Cerrar sesión',
  'common.retry': 'Intentar de nuevo',
  'common.previous': 'Anterior',
  'common.next': 'Siguiente',
  'common.page_of': 'Página {page} de {total}',
  'common.updated': 'Actualizado',
  'common.close': 'Cerrar',
  'common.none': '—',
  'common.new_tab': 'Se abre en una pestaña nueva',

  // Added during the production-readiness pass (keys referenced by pages)
  'lang.persist_failed': 'No se pudo guardar tu idioma en tu perfil. Se aplicará solo en este dispositivo.',
  'locations.map_error': 'No se pudo mostrar el mapa. Usa la lista para abrir una propiedad.',
  'dashboard.renewal_account': 'Próxima renovación: {name} el {date}',
  'dashboard.health_accounts_scored': '{n} de {total} cuentas tienen datos suficientes para puntuar',
  'health.subtitle': 'Comprobaciones en vivo contra el backend del portal. Nada de esto está en caché.',
  'health.checked_at': 'Comprobado a las {time}',
  'location.network.unifi_hint': 'Los recuentos provienen de UniFi Site Manager y se actualizan cada cinco minutos.',
  'location.network.site': 'Sitio',
  'location.reports_error': 'No se pudieron cargar los reportes diarios.',
  'location.activity_error': 'No se pudo cargar la actividad.',
  'staff.picker.eyebrow': 'Personal de PazNetworks',
  'staff.picker.empty_hint': 'Invita a un contacto del cliente desde el espacio de administración para crear el primer acceso al portal.',
  'staff.picker.col_user': 'Usuario del portal',
  'staff.picker.col_accounts': 'Cuentas',
  'tickets.detail.timeline_error': 'No se pudo cargar la cronología del ticket.',
};

export const messages: Record<Locale, Record<Key, string>> = { en, es };
export type MessageKey = Key;

export type MessageParams = Record<string, string | number>;

/** Fills `{name}` placeholders. Unknown placeholders are left untouched. */
export function formatMessage(template: string, params?: MessageParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in params ? String(params[name]) : match,
  );
}
